import { getCorrectPathForArticle, shouldRedirect } from '@/utils/slugResolver'
import {
  fetchArticleWithAllVariations,
  fetchArticleWithErrorHandling,
} from '@/utils/contentfulHelpers'
import { isNonContentPath } from '@/utils/isNonContentPath'

export default defineNuxtRouteMiddleware(async (to) => {
  // Skip on client-side navigation — redirects are already resolved during
  // prerendering and baked into the static output.  Running this middleware in
  // the browser was causing Contentful API calls on every page navigation,
  // rapidly exhausting request limits.
  if (import.meta.client) return

  // Skip non-content paths (bot probes, file extensions, scanners, etc.)
  if (isNonContentPath(to.path)) {
    return
  }

  // Only run on article routes
  const articleRoutes = [
    'hjalp-djuren-category-slug',
    'djurfakta-slug',
    'klubbnytt-slug',
    'vuxen-category-slug',
    'vuxen-slug',
    'om-oss-slug',
    'slug',
  ]

  if (!articleRoutes.includes(to.name as string)) {
    return
  }

  // Skip if no params
  if (!to.params || Object.keys(to.params).length === 0) {
    return
  }

  try {
    // Get the current path
    const currentPath = to.path

    // Determine the path array based on route name
    let pathArray: string[] = []

    switch (to.name) {
      case 'hjalp-djuren-category-slug':
        pathArray = ['hjalp-djuren', to.params.category as string, to.params.slug as string]
        break
      case 'djurfakta-slug':
        pathArray = ['djurfakta', to.params.slug as string]
        break
      case 'klubbnytt-slug':
        pathArray = ['klubbnytt', to.params.slug as string]
        break
      case 'vuxen-category-slug':
        pathArray = ['vuxen', to.params.category as string, to.params.slug as string]
        break
      case 'vuxen-slug':
        pathArray = ['vuxen', to.params.slug as string]
        break
      case 'om-oss-slug':
        pathArray = ['om-oss', to.params.slug as string]
        break
      case 'slug':
        // For catch-all route, use the full path
        pathArray = to.path.split('/').filter(Boolean)
        break
      default:
        return
    }

    // Get the slug for fetching the article
    const slug = pathArray[pathArray.length - 1]
    if (!slug) return

    // Fetch the article using the appropriate method
    let article = null

    try {
      // Try to fetch the article based on route type
      switch (to.name) {
        case 'hjalp-djuren-category-slug':
          article = await fetchArticleWithErrorHandling(
            slug,
            `redirect:article:${to.params.category}:${slug}`,
          )
          break
        case 'djurfakta-slug':
          article = await fetchArticleWithAllVariations(
            slug,
            `djurfakta/${slug}`,
            `redirect:djurfakta:${slug}`,
          )
          break
        case 'klubbnytt-slug':
          article = await fetchArticleWithErrorHandling(slug, `redirect:klubbnytt:${slug}`)
          break
        case 'vuxen-category-slug':
          article = await fetchArticleWithErrorHandling(
            slug,
            `redirect:vuxen-article:${to.params.category}:${slug}`,
          )
          break
        case 'vuxen-slug':
          article = await fetchArticleWithErrorHandling(slug, `redirect:vuxen-article:${slug}`)
          break
        case 'om-oss-slug':
          article = await fetchArticleWithErrorHandling(slug, `redirect:om-oss:${slug}`)
          break
        case 'slug':
          article = await fetchArticleWithAllVariations(
            slug,
            pathArray.join('/'),
            `redirect:article:${pathArray.join('/')}`,
          )
          break
      }
    } catch (error) {
      // If we can't fetch the article, just continue without redirect
      console.warn('Could not fetch article for redirect check:', error)
      return
    }

    // If we found an article, check if we should redirect
    if (article) {
      const correctPath = await getCorrectPathForArticle(article, pathArray)

      if (correctPath && shouldRedirect(currentPath, correctPath)) {
        return navigateTo(correctPath, { redirectCode: 301 })
      }
    }
  } catch (error) {
    // If anything goes wrong, just continue without redirect
    console.warn('Error in article redirect middleware:', error)
  }
})
